import { useState } from "react";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authAPI, getUserData } from "@/lib/api";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Logo } from "@/components/Logo";
import { NotificationDropdown } from "@/components/NotificationDropdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  LogOut,
  User as UserIcon,
  Menu,
} from "lucide-react";

interface NavigationProps {
  user?: any;
  isAdmin?: boolean;
}

export const Navigation = ({ user, isAdmin }: NavigationProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get user from localStorage if not passed as prop
  const currentUser = user || getUserData();

  const handleSignOut = async () => {
    await authAPI.logout();
    navigate("/");
  };

  // Landing page navigation (not logged in)
  if (!currentUser) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center">
            <Logo width={180} height={60} />
          </NavLink>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button
              onClick={() => navigate("/auth")}
              className="bg-primary hover:bg-primary/90"
            >
              {t('nav.signIn')}
            </Button>
          </div>
        </div>
      </header>
    );
  }

  // Admin navigation (no nav items)
  if (currentUser.role === 'admin') {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="container mx-auto h-16 px-3 flex items-center justify-between gap-2 sm:px-6">
          <NavLink to="/" className="flex items-center">
            <Logo width={140} height={48} />
          </NavLink>

          <div className="flex shrink-0 items-center gap-1 sm:gap-3">
            <LanguageSelector />
            <NotificationDropdown />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative h-9 w-9 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src={currentUser?.profileImageDisplayUrl || currentUser?.profileImage || undefined}
                      alt={currentUser?.name || currentUser?.email || t('profile.title')}
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {currentUser?.name?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'A'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <UserIcon className="w-4 h-4 mr-2" />
                  {t('profile.title')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    );
  }

  // Regular user navigation (with nav items)
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="container mx-auto h-16 px-3 sm:px-6 flex items-center justify-between gap-2">
        <NavLink to="/" className="flex items-center">
          <Logo width={140} height={48} />
        </NavLink>

        <nav className="hidden md:flex items-center gap-6">
          <NavLink
            to="/dashboard"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
            activeClassName="text-primary font-medium"
          >
            <LayoutDashboard className="w-4 h-4" />
          </NavLink>
        </nav>

        <div className="flex shrink-0 items-center gap-1 sm:gap-3">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('nav.menu')}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={i18n.language === 'ar' ? 'left' : 'right'} className="w-[min(20rem,85vw)]">
              <SheetHeader>
                <SheetTitle>{t('nav.navigation')}</SheetTitle>
              </SheetHeader>
              <nav className="mt-8 grid gap-2">
                {[
                  { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
                ].map(({ to, icon: Icon, label }) => (
                  <SheetClose key={to} asChild>
                    <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => navigate(to)} aria-label={label}>
                      <Icon className="h-5 w-5" />
                    </Button>
                  </SheetClose>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
          <LanguageSelector />
          <NotificationDropdown />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative h-9 w-9 rounded-full">
                <Avatar className="h-9 w-9">
                  <AvatarImage
                    src={currentUser?.profileImageDisplayUrl || currentUser?.profileImage || undefined}
                    alt={currentUser?.name || currentUser?.email || t('profile.title')}
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {currentUser?.name?.charAt(0).toUpperCase() || currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                <UserIcon className="w-4 h-4 mr-2" />
                {t('profile.title')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
